/**
 * Page `/corpus/:id` — Mission P0 (PR C, partie frontend).
 *
 * Affiche un document source du corpus public (OHADA / CCJA) quand
 * l'utilisateur clique sur une assertion juridique dans ClaimsList.
 *
 * Securite : la route est dans ProtectedRoute (cf. App.tsx). Le backend
 * filtre `is_public=True` strict et retourne 404 sur tout document
 * prive (anti-leak).
 */
import { ArrowLeft, AlertCircle, BookOpen, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "wouter";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { fetchCorpusDocument, type CorpusDocument } from "@/lib/api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; document: CorpusDocument }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

export default function CorpusPage() {
  const params = useParams<{ id: string }>();
  const sourceId = params?.id ?? "";
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!sourceId) {
      setState({ kind: "error", message: "Identifiant de source manquant." });
      return;
    }
    setState({ kind: "loading" });
    fetchCorpusDocument(sourceId)
      .then((document) => {
        if (!cancelled) setState({ kind: "ok", document });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Axios : err.response?.status
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setState({ kind: "not_found" });
        } else if (status === 401) {
          setState({
            kind: "error",
            message: "Session expiree. Veuillez vous reconnecter.",
          });
        } else {
          const msg = (err as Error)?.message ?? "Erreur de chargement.";
          setState({ kind: "error", message: msg });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  return (
    <main className="min-h-screen px-4 py-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link href="/recherche">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft size={16} />
            Retour à la recherche
          </Button>
        </Link>
      </div>

      {state.kind === "loading" && (
        <Card>
          <CardContent className="py-12 flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="animate-spin" size={20} />
            <span>Chargement du document...</span>
          </CardContent>
        </Card>
      )}

      {state.kind === "not_found" && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto mb-4 text-amber-500" size={40} />
            <h1 className="text-xl font-semibold mb-2">Document introuvable</h1>
            <p className="text-slate-600 max-w-md mx-auto">
              Cette source n'est pas accessible. Elle a peut-etre ete retiree
              du corpus public, ou l'identifiant n'est pas valide.
            </p>
            <p
              className="text-xs text-slate-400 mt-4 font-mono break-all"
              data-testid="corpus-missing-id"
            >
              ID : {sourceId}
            </p>
          </CardContent>
        </Card>
      )}

      {state.kind === "error" && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={40} />
            <h1 className="text-xl font-semibold mb-2">
              Erreur de chargement
            </h1>
            <p className="text-slate-600">{state.message}</p>
          </CardContent>
        </Card>
      )}

      {state.kind === "ok" && (
        <Card>
          <CardContent className="py-6">
            <header className="mb-6 pb-4 border-b border-slate-200">
              <div className="flex items-start gap-3 mb-3">
                <BookOpen
                  className="mt-1 shrink-0"
                  size={24}
                  style={{ color: "var(--proof-gold, #b08c4a)" }}
                  aria-hidden="true"
                />
                <div className="flex-1">
                  <h1 className="text-2xl font-semibold text-slate-900 leading-tight">
                    {state.document.source}
                  </h1>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    {state.document.source_type && (
                      <span
                        className="px-2 py-0.5 rounded font-mono"
                        style={{
                          background: "var(--graphite-100, #f5f5f5)",
                          color: "var(--graphite-600, #555)",
                        }}
                      >
                        {state.document.source_type}
                      </span>
                    )}
                    {state.document.jurisdiction && (
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {state.document.jurisdiction}
                      </span>
                    )}
                    {state.document.legal_force && (
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {state.document.legal_force}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded font-mono text-slate-400">
                      {state.document.id}
                    </span>
                  </div>
                </div>
              </div>
            </header>

            <article
              className="prose prose-slate max-w-none whitespace-pre-wrap text-slate-800 leading-relaxed"
              data-testid="corpus-content"
            >
              {state.document.content}
            </article>
          </CardContent>
        </Card>
      )}
    </main>
  );
}
